import React, { useState, useEffect } from "react";
import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { Table, Button, Form, Row, Col, Modal } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { selectGlobalVariables, selectGlobalVariablesStatus, fetchGlobalVariables } from "../../redux/slices/globalVariablesSlice";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { postData, deleteData, putData } from "../../redux/slices/apiCallSlice";
import AdminNav from "./AdminNav";
import TopNav from "../TopNav/TopNav";
import { usePermissions } from "../../hooks/usePermissions";

function AdminGlobalVariables() {
  const dispatch = useDispatch();
  const globalVariables = useSelector(selectGlobalVariables);
  const status = useSelector(selectGlobalVariablesStatus);
  const apiUrls = useSelector(selectApiUrls);
  const globalVariablesUrl = apiUrls.globalVariables;
  const { canView, canEdit, isLoading: isPermissionsLoading } = usePermissions();
  const canViewGlobalVariables = canView("admin.global-variables");
  const canEditGlobalVariables = canEdit("admin.global-variables");

  const [editItem, setEditItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  useRealtimeDraft(Boolean(editItem || showModal));
  const [newItem, setNewItem] = useState({
    Title: "",
    VariableValue: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch global variables on component mount
  useEffect(() => {
    dispatch(fetchGlobalVariables());
  }, [dispatch]);

  // Function to handle opening the edit modal
  const handleEdit = (item) => {
    if (!canEditGlobalVariables) {
      return;
    }

    setEditItem({ ...item });
    setShowModal(true);
  };

  // Function to handle opening the add modal
  const handleAddNew = () => {
    if (!canEditGlobalVariables) {
      return;
    }

    setEditItem(null);
    setNewItem({
      Title: "",
      VariableValue: "",
    });
    setShowModal(true);
  };

  // Function to handle saving changes (edit or add)
  const handleSave = async () => {
    if (!canEditGlobalVariables) {
      return;
    }

    try {
      if (editItem) {
        // Update existing item
        await dispatch(
          putData({
            url: globalVariablesUrl,
            data: editItem,
          })
        ).unwrap();
        setSuccessMessage("Global variable updated successfully");
      } else {
        // Add new item
        await dispatch(
          postData({
            url: globalVariablesUrl,
            data: newItem,
          })
        ).unwrap();
        setSuccessMessage("Global variable added successfully");
      }
      
      // Refresh the data
      dispatch(fetchGlobalVariables());
      setShowModal(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error saving global variable:", error);
      setErrorMessage(error.message || "Failed to save global variable");
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
    }
  };

  // Function to handle deleting an item
  const handleDelete = async (id) => {
    if (!canEditGlobalVariables) {
      return;
    }

    if (window.confirm("Are you sure you want to delete this global variable?")) {
      try {
        await dispatch(
          deleteData({
            url: globalVariablesUrl,
            deleteId: { ID: id },
          })
        ).unwrap();
        
        // Refresh the data
        dispatch(fetchGlobalVariables());
        setSuccessMessage("Global variable deleted successfully");
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      } catch (error) {
        console.error("Error deleting global variable:", error);
        setErrorMessage(error.message || "Failed to delete global variable");
        
        // Clear error message after 3 seconds
        setTimeout(() => {
          setErrorMessage("");
        }, 3000);
      }
    }
  };

  // Function to handle input changes in the modal
  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    if (editItem) {
      setEditItem({
        ...editItem,
        [name]: value,
      });
    } else {
      setNewItem({
        ...newItem,
        [name]: value,
      });
    }
  };

  // Function to handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setErrorMessage("");
  };

  // Render loading state
  if (status === "loading" || isPermissionsLoading) {
    return (
      <div>
        <TopNav />
        <AdminNav keyValue="adminGlobalVariables" />
        <div className="container mt-4">
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canViewGlobalVariables) {
    return <div className="alert alert-warning m-4">You do not have permission to view global variables.</div>;
  }

  return (
    <div>
      <div className="m-4">
        <h2>Manage Global Variables</h2>
        
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="alert alert-success">{successMessage}</div>
        )}
        {errorMessage && (
          <div className="alert alert-danger">{errorMessage}</div>
        )}

        {/* Add Button */}
        {canEditGlobalVariables && (
          <Button 
            variant="primary" 
            onClick={handleAddNew} 
            className="mb-3"
          >
            Add New Global Variable
          </Button>
        )}

        {/* Global Variables Table */}
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Value</th>
              {canEditGlobalVariables && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {globalVariables.map((item) => (
              <tr key={item.ID}>
                <td>{item.ID}</td>
                <td>{item.Title}</td>
                <td>{item.VariableValue}</td>
                {canEditGlobalVariables && (
                  <td>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={() => handleEdit(item)}
                      className="me-2"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(item.ID)}
                    >
                      Delete
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {globalVariables.length === 0 && (
              <tr>
                <td colSpan={canEditGlobalVariables ? 4 : 3} className="text-center">
                  No global variables found
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        {/* Edit/Add Modal */}
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editItem ? "Edit Global Variable" : "Add New Global Variable"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3}>Title</Form.Label>
                <Col sm={9}>
                  <Form.Control
                    type="text"
                    name="Title"
                    value={editItem ? editItem.Title : newItem.Title}
                    onChange={handleModalInputChange}
                    required
                  />
                </Col>
              </Form.Group>
              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={3}>Value</Form.Label>
                <Col sm={9}>
                  <Form.Control
                    type="text"
                    name="VariableValue"
                    value={editItem ? editItem.VariableValue : newItem.VariableValue}
                    onChange={handleModalInputChange}
                    required
                  />
                </Col>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={!canEditGlobalVariables}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
}

export default AdminGlobalVariables;
